import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Box,
  Paper,
  Typography,
  Button,
  Drawer,
  Divider,
  IconButton,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Save as SaveIcon,
  PlayArrow as RunIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Code as CodeIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  ContentCopy as CopyIcon,
  ContentPaste as PasteIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  AutoFixHigh as AutoFixHighIcon,
  FileCopy as DuplicateIcon,
} from '@mui/icons-material';
import api from '../utils/api';
import { MODULE_TYPES, NODE_COLORS, PIPELINE_STATUS } from '../utils/constants';
import NodeConfigPanel from '../components/pipeline/NodeConfigPanel';
import NodeSelector from '../components/pipeline/NodeSelector';
import CustomNode from '../components/pipeline/CustomNode';
import useUndoRedo from '../hooks/useUndoRedo';
import GeneratePipelineDialog from '../components/pipeline/GeneratePipelineDialog';
import CodeNodeConfig from '../components/modules/CodeNodeConfig';
import { CODE_MODULE, initialNodeConfig } from '../components/modules/codeModules';

// Node types for ReactFlow
const nodeTypes = {
  customNode: CustomNode,
};

const PipelineDesignerPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const reactFlowWrapper = useRef(null);
  // States
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const selectedNode = selectedNodeId ? nodes.find(n => n.id === selectedNodeId) || null : null;
  const [isModuleSelectorOpen, setIsModuleSelectorOpen] = useState(false);
  const [modules, setModules] = useState({});
  const [pipeline, setPipeline] = useState({
    name: '',
    description: '',
    status: PIPELINE_STATUS.DRAFT,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPipelineSettingsOpen, setIsPipelineSettingsOpen] = useState(false);
  const [isRunDialogOpen, setIsRunDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);
  const [clipboard, setClipboard] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const { pushState, undo, redo, canUndo, canRedo } = useUndoRedo();

  // Public pipelines owned by someone else open read-only (can_edit comes from the API)
  const canEdit = id === 'new' || pipeline.can_edit !== false;

  // Keyboard shortcuts (Ctrl+Z, Ctrl+Shift+Z, Ctrl+C, Ctrl+V)
  useEffect(() => {
    const handler = (e) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod || !canEdit) return;

      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo) undo(nodes, edges, setNodes, setEdges);
      } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
        e.preventDefault();
        if (canRedo) redo(nodes, edges, setNodes, setEdges);
      } else if (e.key === 'c' && selectedNode) {
        setClipboard(structuredClone(selectedNode));
      } else if (e.key === 'v' && clipboard) {
        e.preventDefault();
        const newNode = {
          ...clipboard,
          id: `new_${Date.now()}`,
          position: { x: clipboard.position.x + 50, y: clipboard.position.y + 50 },
          data: { ...clipboard.data },
        };
        pushState(nodes, edges);
        setNodes((nds) => nds.concat(newNode));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [canEdit, canUndo, canRedo, nodes, edges, setNodes, setEdges, undo, redo, selectedNode, clipboard, pushState]);

  // Load pipeline data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch available pipeline modules (registered modules with config schemas)
        const modulesResponse = await api.get('/pipeline-modules');

        // The pipeline-modules endpoint returns {modules: [...]} with correct types
        // and config_schema already converted for the frontend
        const modules = modulesResponse.data.modules || [];

        const transformedModules = modules.map(module => ({
          id: module.id,
          name: module.name,
          description: module.description,
          type: module.type,
          module_name: module.module_name,
          config_schema: module.config_schema,
          version: module.version,
          source: module.source || 'built_in',
          custom_module_id: module.custom_module_id || null,
          preset_config: module.preset_config || {},
        }));

        setModules(transformedModules);
        
        // If editing existing pipeline, fetch its data
        if (id && id !== 'new') {
          const pipelineResponse = await api.get(`/pipelines/${id}`);
          
          const pipelineData = pipelineResponse.data;
          
          // Set pipeline metadata
          setPipeline({
            id: pipelineData.id,
            name: pipelineData.name,
            description: pipelineData.description || '',
            status: pipelineData.status,
            config: pipelineData.config || {},
            visibility: pipelineData.visibility || 'private',
            owner_username: pipelineData.owner_username,
            can_edit: pipelineData.can_edit !== false,
          });
          
          // Convert backend nodes to ReactFlow format
          const flowNodes = pipelineData.nodes.map(node => ({
            id: node.id.toString(),
            type: 'customNode',
            position: { x: node.position_x || 0, y: node.position_y || 0 },
            data: { 
              label: node.name,
              type: node.type,
              module: node.module_name,
              config: node.config || {},
              nodeColors: NODE_COLORS[node.type],
            },
          }));
          
          // Convert backend edges to ReactFlow format
          const flowEdges = pipelineData.edges.map(edge => ({
            id: edge.id.toString(),
            source: edge.source_id.toString(),
            target: edge.target_id.toString(),
            sourceHandle: edge.source_handle || null,
            targetHandle: edge.target_handle || null,
          }));
          
          setNodes(flowNodes);
          setEdges(flowEdges);
        } else {
          // For new pipeline, reset nodes and edges
          setNodes([]);
          setEdges([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setSnackbar({
          open: true,
          message: 'Error loading pipeline data',
          severity: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [id, setNodes, setEdges]);
  
  // Handle connection between nodes
  const onConnect = useCallback((params) => {
    if (!canEdit) return;
    pushState(nodes, edges);
    setEdges((eds) => addEdge(params, eds));
  }, [canEdit, setEdges, pushState, nodes, edges]);
  
  // Handle node selection
  const onNodeClick = useCallback((event, node) => {
    setSelectedNodeId(node.id);
    setActiveTab(0); // Switch to properties tab
  }, []);
  
  // Handle adding a new node
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);
  
  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      if (!canEdit || !reactFlowInstance || !reactFlowWrapper.current) {
        return;
      }

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const moduleData = JSON.parse(event.dataTransfer.getData('application/reactflow'));

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNode = {
        id: `new_${Date.now()}`,
        type: 'customNode',
        position,
        data: {
          label: moduleData.name,
          type: moduleData.type,
          module: moduleData.module,
          customModuleId: moduleData.customModuleId || null,
          config: initialNodeConfig(moduleData.module, moduleData.customModuleId, moduleData.presetConfig),
          nodeColors: NODE_COLORS[moduleData.type],
        },
      };

      pushState(nodes, edges);
      setNodes((nds) => nds.concat(newNode));
    },
    [canEdit, reactFlowInstance, setNodes, pushState, nodes, edges]
  );
  
  // Add a module node from dialog
  const addModuleNode = useCallback((module) => {
    if (!canEdit) return;
    // Place node near center of viewport
    let position = { x: 250, y: 150 };
    if (reactFlowInstance) {
      const { x, y, zoom } = reactFlowInstance.getViewport();
      const wrapper = reactFlowWrapper.current;
      if (wrapper) {
        const rect = wrapper.getBoundingClientRect();
        position = reactFlowInstance.project({
          x: rect.width / 2,
          y: rect.height / 2,
        });
      }
    }

    const newNode = {
      id: `new_${Date.now()}`,
      type: 'customNode',
      position,
      data: {
        label: module.name,
        type: module.type,
        module: module.module_name,
        customModuleId: module.custom_module_id || null,
        config: initialNodeConfig(module.module_name, module.custom_module_id, module.preset_config),
        nodeColors: NODE_COLORS[module.type],
      },
    };

    pushState(nodes, edges);
    setNodes((nds) => nds.concat(newNode));
  }, [canEdit, reactFlowInstance, pushState, nodes, edges, setNodes]);

  // Handle AI-generated pipeline
  const handlePipelineGenerated = useCallback((data) => {
    // Set pipeline name and description
    setPipeline(prev => ({
      ...prev,
      name: data.name || prev.name,
      description: data.description || prev.description,
    }));

    // Convert generated nodes to ReactFlow format
    const now = Date.now();
    const tempIdToFlowId = {};

    const flowNodes = (data.nodes || []).map((node, idx) => {
      const flowId = `new_${now}_${idx}`;
      tempIdToFlowId[node.temp_id] = flowId;
      return {
        id: flowId,
        type: 'customNode',
        position: { x: node.position_x || 0, y: node.position_y || 0 },
        data: {
          label: node.name,
          type: node.type,
          module: node.module_name,
          config: node.config || {},
          nodeColors: NODE_COLORS[node.type],
        },
      };
    });

    // Map edges using temp_id -> flowId
    const flowEdges = (data.edges || []).map((edge, idx) => ({
      id: `gen_edge_${now}_${idx}`,
      source: tempIdToFlowId[edge.source_temp_id] || edge.source_temp_id,
      target: tempIdToFlowId[edge.target_temp_id] || edge.target_temp_id,
    }));

    pushState(nodes, edges);
    setNodes(flowNodes);
    setEdges(flowEdges);

    // Show snackbar with ai_notes if present
    const message = data.ai_notes
      ? `Pipeline generated. Notes: ${data.ai_notes}`
      : 'Pipeline generated successfully!';
    setSnackbar({
      open: true,
      message,
      severity: data.ai_notes ? 'warning' : 'success',
    });
  }, [pushState, nodes, edges, setNodes, setEdges]);

  // Save pipeline
  const savePipeline = async () => {
    if (!canEdit) return;
    setIsSaving(true);
    try {
      // Prepare payload
      const payload = {
        name: pipeline.name,
        description: pipeline.description,
        status: pipeline.status,
        config: pipeline.config || {},
      };
      
      let response;
      let pipelineId = id && id !== 'new' ? id : null;
      
      // If this is a new pipeline, create it first
      if (!pipelineId) {
        response = await api.post('/pipelines', payload);
        pipelineId = response.data.id;
      } else {
        // Update existing pipeline
        response = await api.put(`/pipelines/${pipelineId}`, payload);
      }
      
      // Create a mapping of frontend node IDs to backend node IDs
      const nodeIdMap = new Map();

      // Determine if a node is new (not yet saved to DB)
      const isNewNode = (nodeId) => String(nodeId).startsWith('new_');

      // First, save all nodes and build the ID mapping
      for (const node of nodes) {
        const nodeData = {
          name: node.data.label,
          type: node.data.type,
          module_name: node.data.module,
          position_x: Math.round(node.position.x),
          position_y: Math.round(node.position.y),
          config: node.data.config,
        };

        let savedNode;
        if (isNewNode(node.id)) {
          // Create new node
          savedNode = await api.post(
            `/pipelines/${pipelineId}/nodes`,
            nodeData
          );
        } else {
          // Update existing node
          try {
            savedNode = await api.put(
              `/pipelines/${pipelineId}/nodes/${node.id}`,
              nodeData
            );
          } catch (err) {
            // If 404, create instead
            if (err.response?.status === 404) {
              savedNode = await api.post(
                `/pipelines/${pipelineId}/nodes`,
                nodeData
              );
            } else {
              throw err;
            }
          }
        }

        const backendId = savedNode.data.id;
        nodeIdMap.set(node.id, backendId.toString());

        // Update local state with DB ID
        if (node.id !== backendId.toString()) {
          setNodes(nds => nds.map(n =>
            n.id === node.id ? { ...n, id: backendId.toString() } : n
          ));
          // Also update edges that reference this node
          setEdges(eds => eds.map(e => ({
            ...e,
            source: e.source === node.id ? backendId.toString() : e.source,
            target: e.target === node.id ? backendId.toString() : e.target,
          })));
        }
      }

      // Get existing edges from DB
      const existingEdgesResponse = await api.get(`/pipelines/${pipelineId}/edges`);
      const existingEdges = existingEdgesResponse.data || [];

      // Track which DB edges are still on the canvas
      const keptEdgeDbIds = new Set();

      // Save edges with mapped node IDs
      for (const edge of edges) {
        const sourceId = nodeIdMap.get(edge.source) || edge.source;
        const targetId = nodeIdMap.get(edge.target) || edge.target;

        const edgeData = {
          source_id: parseInt(sourceId, 10),
          target_id: parseInt(targetId, 10),
          source_handle: edge.sourceHandle || null,
          target_handle: edge.targetHandle || null,
        };

        // Find matching existing edge
        const existingEdge = existingEdges.find(e =>
          e.source_id === edgeData.source_id && e.target_id === edgeData.target_id
        );

        try {
          if (existingEdge) {
            await api.put(
              `/pipelines/${pipelineId}/edges/${existingEdge.id}`,
              edgeData
            );
            keptEdgeDbIds.add(existingEdge.id);
            // Update local ID
            setEdges(eds => eds.map(e =>
              e.id === edge.id ? { ...e, id: existingEdge.id.toString() } : e
            ));
          } else {
            const response = await api.post(
              `/pipelines/${pipelineId}/edges`,
              edgeData
            );
            keptEdgeDbIds.add(response.data.id);
            setEdges(eds => eds.map(e =>
              e.id === edge.id ? { ...e, id: response.data.id.toString() } : e
            ));
          }
        } catch (error) {
          console.error('Error saving edge:', error);
          if (error.response?.status === 404) {
            const response = await api.post(
              `/pipelines/${pipelineId}/edges`,
              edgeData
            );
            keptEdgeDbIds.add(response.data.id);
            setEdges(eds => eds.map(e =>
              e.id === edge.id ? { ...e, id: response.data.id.toString() } : e
            ));
          } else {
            throw error;
          }
        }
      }

      // Delete edges that were removed from the canvas
      for (const dbEdge of existingEdges) {
        if (!keptEdgeDbIds.has(dbEdge.id)) {
          try {
            await api.delete(`/pipelines/${pipelineId}/edges/${dbEdge.id}`);
          } catch (err) {
            console.warn('Failed to delete orphaned edge:', err);
          }
        }
      }

      // Delete nodes that were removed from the canvas (their edges went above;
      // the API refuses to delete a node that still has edges).
      const savedNodeIds = new Set(nodeIdMap.values());
      const serverPipeline = await api.get(`/pipelines/${pipelineId}`);
      for (const dbNode of serverPipeline.data?.nodes || []) {
        if (!savedNodeIds.has(String(dbNode.id))) {
          await api.delete(`/pipelines/${pipelineId}/nodes/${dbNode.id}`);
        }
      }

      // Navigate to the newly created pipeline if it's a new pipeline
      if (!id || id === 'new') {
        navigate(`/pipelines/${pipelineId}`);
      }
      
      setSnackbar({
        open: true,
        message: 'Pipeline saved successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error saving pipeline:', error);
      setSnackbar({
        open: true,
        message: 'Error saving pipeline',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  // Change visibility (owner/admin only); the PUT touches nothing else
  const changeVisibility = async (visibility) => {
    try {
      await api.put(`/pipelines/${id}`, { visibility });
      setPipeline(prev => ({ ...prev, visibility }));
    } catch (error) {
      console.error('Error changing visibility:', error);
      setSnackbar({ open: true, message: 'Error changing visibility', severity: 'error' });
    }
  };

  // Make your own (private) copy of a read-only pipeline
  const duplicatePipeline = async () => {
    try {
      const response = await api.post(`/pipelines/${id}/duplicate`, {
        source_id: Number(id),
        new_name: `Copy of ${pipeline.name}`,
      });
      navigate(`/pipelines/${response.data.id}`);
    } catch (error) {
      console.error('Error duplicating pipeline:', error);
      setSnackbar({ open: true, message: 'Error duplicating pipeline', severity: 'error' });
    }
  };

  // Run pipeline
  const runPipeline = async () => {
    try {
      await api.post('/runs', { pipeline_id: id });
      
      setIsRunDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Pipeline started successfully',
        severity: 'success',
      });
      
      // Navigate to runs page for this pipeline
      navigate(`/runs?pipeline_id=${id}`);
    } catch (error) {
      console.error('Error running pipeline:', error);
      setSnackbar({
        open: true,
        message: 'Error starting pipeline run',
        severity: 'error',
      });
    }
  };
  
  // Handle pipeline settings changes
  const handlePipelineSettingChange = (field, value) => {
    setPipeline(prev => ({ ...prev, [field]: value }));
  };
  
  // Handle node config changes
  const updateNodeConfig = (nodeId, config) => {
    setNodes(nds =>
      nds.map(node => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              config: { ...config },
            },
          };
        }
        return node;
      })
    );
  };
  
  // Handle node deletion
  const deleteSelectedNode = () => {
    if (selectedNode) {
      pushState(nodes, edges);
      setNodes(nds => nds.filter(node => node.id !== selectedNode.id));
      setEdges(eds => eds.filter(edge =>
        edge.source !== selectedNode.id && edge.target !== selectedNode.id
      ));
      setSelectedNodeId(null);
    }
  };

  // Copy selected node
  const copySelectedNode = () => {
    if (selectedNode) {
      setClipboard(structuredClone(selectedNode));
      setSnackbar({ open: true, message: 'Node copied', severity: 'info' });
    }
  };

  // Paste copied node
  const pasteNode = () => {
    if (!clipboard) return;
    const newNode = {
      ...clipboard,
      id: `new_${Date.now()}`,
      position: { x: clipboard.position.x + 50, y: clipboard.position.y + 50 },
      data: { ...clipboard.data },
    };
    pushState(nodes, edges);
    setNodes((nds) => nds.concat(newNode));
    setSnackbar({ open: true, message: 'Node pasted', severity: 'info' });
  };
  
  // Close inspector when clicking on canvas
  const onPaneClick = () => {
    setSelectedNodeId(null);
  };
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <AppBar position="static" color="default" sx={{ boxShadow: 1 }}>
        <Toolbar variant="dense">
          <IconButton
            onClick={() => navigate('/pipelines')}
            size="small"
            title="Back to pipelines"
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>

          {isEditingName ? (
            <TextField
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onBlur={() => {
                if (editingName.trim()) {
                  handlePipelineSettingChange('name', editingName.trim());
                }
                setIsEditingName(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (editingName.trim()) {
                    handlePipelineSettingChange('name', editingName.trim());
                  }
                  setIsEditingName(false);
                } else if (e.key === 'Escape') {
                  setIsEditingName(false);
                }
              }}
              autoFocus
              size="small"
              variant="standard"
              sx={{ flexGrow: 1, mr: 2 }}
              inputProps={{ style: { fontSize: '1.25rem', fontWeight: 500 } }}
            />
          ) : (
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover .edit-icon': { opacity: 1 },
              }}
              onClick={() => {
                if (!canEdit) return;
                setEditingName(pipeline.name || '');
                setIsEditingName(true);
              }}
            >
              <Typography variant="h6" sx={{ mr: 0.5 }}>
                {id === 'new' && !pipeline.name ? 'New Pipeline' : pipeline.name || 'Untitled Pipeline'}
              </Typography>
              <EditIcon
                className="edit-icon"
                fontSize="small"
                color="action"
                sx={{ opacity: 0, transition: 'opacity 0.2s' }}
              />
            </Box>
          )}

          <IconButton
            onClick={() => undo(nodes, edges, setNodes, setEdges)}
            disabled={!canEdit || !canUndo}
            size="small"
            title="Undo (Ctrl+Z)"
          >
            <UndoIcon />
          </IconButton>
          <IconButton
            onClick={() => redo(nodes, edges, setNodes, setEdges)}
            disabled={!canEdit || !canRedo}
            size="small"
            title="Redo (Ctrl+Shift+Z)"
            sx={{ mr: 1 }}
          >
            <RedoIcon />
          </IconButton>
          <IconButton
            onClick={copySelectedNode}
            disabled={!selectedNode}
            size="small"
            title="Copy (Ctrl+C)"
          >
            <CopyIcon />
          </IconButton>
          <IconButton
            onClick={pasteNode}
            disabled={!canEdit || !clipboard}
            size="small"
            title="Paste (Ctrl+V)"
            sx={{ mr: 1 }}
          >
            <PasteIcon />
          </IconButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          <Button
            startIcon={<AutoFixHighIcon />}
            onClick={() => setIsGenerateDialogOpen(true)}
            disabled={!canEdit}
            sx={{ mr: 1 }}
          >
            AI Generate
          </Button>

          <Button
            startIcon={<SettingsIcon />}
            onClick={() => setIsPipelineSettingsOpen(true)}
            disabled={!canEdit}
            sx={{ mr: 1 }}
          >
            Settings
          </Button>
          
          {id !== 'new' && canEdit && (
            <FormControlLabel
              sx={{ mr: 1 }}
              control={
                <Switch
                  size="small"
                  checked={pipeline.visibility === 'public'}
                  onChange={(e) => changeVisibility(e.target.checked ? 'public' : 'private')}
                  inputProps={{ 'aria-label': 'Public' }}
                />
              }
              label={pipeline.visibility === 'public' ? 'Public' : 'Private'}
            />
          )}

          <Button
            startIcon={<RunIcon />}
            onClick={() => setIsRunDialogOpen(true)}
            disabled={id === 'new' || pipeline.status !== PIPELINE_STATUS.ACTIVE}
            color="success"
            variant="contained"
            sx={{ mr: 1 }}
          >
            Run
          </Button>
          
          <Button
            startIcon={<SaveIcon />}
            onClick={savePipeline}
            disabled={!canEdit || isSaving || !pipeline.name}
            color="primary"
            variant="contained"
          >
            {isSaving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </Toolbar>
      </AppBar>

      {!canEdit && (
        <Alert
          severity="info"
          sx={{ borderRadius: 0 }}
          action={
            <Button color="inherit" size="small" startIcon={<DuplicateIcon />} onClick={duplicatePipeline}>
              Duplicate
            </Button>
          }
        >
          Read-only — owned by {pipeline.owner_username}. Duplicate to make your own copy.
        </Alert>
      )}
      
      {/* Main content */}
      <Box sx={{ display: 'flex', flexGrow: 1, height: 'calc(100% - 48px)' }}>
        {/* Module selector panel */}
        <Drawer
          variant="permanent"
          sx={{
            width: 260,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 260,
              position: 'relative',
              height: '100%',
              border: 'none',
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          <Box sx={{ px: 2, py: 2 }}>
            <Typography variant="h6" gutterBottom>
              Modules
            </Typography>
            <Button
              variant="contained"
              fullWidth
              startIcon={<AddIcon />}
              onClick={() => setIsModuleSelectorOpen(true)}
              disabled={!canEdit}
              sx={{ mb: 2 }}
            >
              Add Module
            </Button>
          </Box>
          <Divider />
          <NodeSelector modules={modules} onAddNode={addModuleNode} />
        </Drawer>
        
        {/* Flow editor */}
        <Box sx={{ flexGrow: 1, height: '100%' }} ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            onInit={setReactFlowInstance}
            nodesDraggable={canEdit}
            nodesConnectable={canEdit}
            edgesUpdatable={canEdit}
            deleteKeyCode={canEdit ? 'Backspace' : null}
            onDrop={onDrop}
            onDragOver={onDragOver}
            fitView
          >
            <Background />
            <Controls />
            <MiniMap
              nodeStrokeColor={(n) => {
                if (n.data?.nodeColors?.border) {
                  return n.data.nodeColors.border;
                }
                return '#eee';
              }}
              nodeColor={(n) => {
                if (n.data?.nodeColors?.background) {
                  return n.data.nodeColors.background;
                }
                return '#fff';
              }}
            />
          </ReactFlow>
        </Box>
        
        {/* Inspector panel */}
        {selectedNode && (
          <Drawer
            variant="permanent"
            anchor="right"
            sx={{
              width: 350,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: 350,
                position: 'relative',
                height: '100%',
                border: 'none',
                borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
              <Typography variant="h6">Inspector</Typography>
              <IconButton onClick={() => setSelectedNodeId(null)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider />
            
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1">{selectedNode.data.label}</Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {selectedNode.data.module} ({selectedNode.data.type})
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Node Name"
                  value={selectedNode.data.label}
                  onChange={(e) => {
                    const newLabel = e.target.value;
                    setNodes(nds =>
                      nds.map(node => {
                        if (node.id === selectedNode.id) {
                          return {
                            ...node,
                            data: {
                              ...node.data,
                              label: newLabel,
                            },
                          };
                        }
                        return node;
                      })
                    );
                  }}
                  fullWidth
                  margin="normal"
                  size="small"
                  disabled={!canEdit}
                />
              </Box>
              
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{ mb: 2 }}
              >
                <Tab label="Properties" />
                <Tab label="Advanced" />
              </Tabs>
              
              {activeTab === 0 && selectedNode.data.module === CODE_MODULE && (
                <CodeNodeConfig
                  key={selectedNode.id}
                  config={selectedNode.data.config}
                  modules={modules}
                  canEdit={canEdit}
                  onConfigChange={(config) => updateNodeConfig(selectedNode.id, config)}
                />
              )}

              {activeTab === 0 && selectedNode.data.module !== CODE_MODULE && (
                <fieldset
                  disabled={!canEdit}
                  style={{
                    border: 0, margin: 0, padding: 0, minWidth: 0,
                    ...(canEdit ? {} : { pointerEvents: 'none', opacity: 0.7 }),
                  }}
                >
                <NodeConfigPanel
                  nodeId={selectedNode.id}
                  nodeType={selectedNode.data.type}
                  moduleType={selectedNode.data.type}
                  moduleName={selectedNode.data.module}
                  config={selectedNode.data.config}
                  onConfigChange={updateNodeConfig}
                  modules={modules}
                  selectedNode={selectedNode}
                />
                </fieldset>
              )}
              
              {activeTab === 1 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Advanced Settings
                  </Typography>
                  
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={deleteSelectedNode}
                      disabled={!canEdit}
                      fullWidth
                    >
                      Delete Node
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Drawer>
        )}
      </Box>
      
      {/* Pipeline Settings Dialog */}
      <Dialog
        open={isPipelineSettingsOpen}
        onClose={() => setIsPipelineSettingsOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Pipeline Settings</DialogTitle>
        <DialogContent>
          <TextField
            label="Pipeline Name"
            value={pipeline.name}
            onChange={(e) => handlePipelineSettingChange('name', e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Description"
            value={pipeline.description}
            onChange={(e) => handlePipelineSettingChange('description', e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={3}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={pipeline.status}
              onChange={(e) => handlePipelineSettingChange('status', e.target.value)}
              label="Status"
            >
              <MenuItem value={PIPELINE_STATUS.DRAFT}>Draft</MenuItem>
              <MenuItem value={PIPELINE_STATUS.ACTIVE}>Active</MenuItem>
              <MenuItem value={PIPELINE_STATUS.PAUSED}>Paused</MenuItem>
              <MenuItem value={PIPELINE_STATUS.ARCHIVED}>Archived</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={pipeline.config?.store_node_data !== false}
                onChange={(e) => handlePipelineSettingChange('config', {
                  ...(pipeline.config || {}),
                  store_node_data: e.target.checked,
                })}
              />
            }
            label="Store step data for debugging"
          />
          <Typography variant="caption" color="text.secondary" component="p">
            Keeps a sample (up to 50 records) of what each step received and produced, shown under Runs → Node Execution.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPipelineSettingsOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              setIsPipelineSettingsOpen(false);
              savePipeline();
            }}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Run Pipeline Dialog */}
      <Dialog
        open={isRunDialogOpen}
        onClose={() => setIsRunDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Run Pipeline</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to run pipeline "{pipeline.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This will execute all nodes in the pipeline with current configurations.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRunDialogOpen(false)}>Cancel</Button>
          <Button onClick={runPipeline} variant="contained" color="primary">
            Run
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Module Selector Dialog */}
      <Dialog
        open={isModuleSelectorOpen}
        onClose={() => setIsModuleSelectorOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Select Module</DialogTitle>
        <DialogContent>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ mb: 2 }}
          >
            <Tab label="Input Modules" />
            <Tab label="Transform Modules" />
            <Tab label="AI Enrichment" />
            <Tab label="Output Modules" />
          </Tabs>
          
          <Box sx={{ mt: 2 }}>
            <NodeSelector
              modules={modules}
              selectedType={
                activeTab === 0
                  ? MODULE_TYPES.INPUT
                  : activeTab === 1
                  ? MODULE_TYPES.TRANSFORM
                  : activeTab === 2
                  ? MODULE_TYPES.AI_ENRICHMENT
                  : MODULE_TYPES.OUTPUT
              }
              inDialog={true}
              onSelect={() => setIsModuleSelectorOpen(false)}
              onAddNode={addModuleNode}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsModuleSelectorOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* AI Generate Pipeline Dialog */}
      <GeneratePipelineDialog
        open={isGenerateDialogOpen}
        onClose={() => setIsGenerateDialogOpen(false)}
        onPipelineGenerated={handlePipelineGenerated}
      />

      {/* Snackbar notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PipelineDesignerPage;
